import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import {
  LaneBerthReservation,
  LaneManualAssignment,
  ShipmentOrderLaneDetail,
} from 'src/entities/ShipmentOrderLane';
import {
  useMutationShipmentOrderLane,
  UpdateShipmentOrderLaneRequest,
} from 'src/hooks/useShipmentOrderLanes';

export type EditableAssignment = LaneManualAssignment & {
  tempId: string;
};

export type EditableBerthReservation = LaneBerthReservation & {
  tempId: string;
};

type UseLaneDetailEditReturn = {
  // Edit state
  isEditing: boolean;
  isSaving: boolean;

  // Editable data
  requiredTruckCount: number | null;
  berthReservations: EditableBerthReservation[];
  manualAssignments: EditableAssignment[];
  modified: boolean;

  // Actions
  startEditing: () => void;
  cancelEditing: () => void;
  saveChanges: () => void;

  // Field updates
  setRequiredTruckCount: (value: number | null) => void;
  updateBerthReservation: (tempId: string, field: keyof Omit<LaneBerthReservation, 'id'>, value: string | null) => void;
  addBerthReservation: (kind: 'loading' | 'unloading') => void;
  removeBerthReservation: (tempId: string) => void;
  updateManualAssignment: (tempId: string, field: keyof Omit<LaneManualAssignment, 'id'>, value: string | number | null) => void;
  addManualAssignment: () => void;
  removeManualAssignment: (tempId: string) => void;
};

/**
 * レーン詳細編集用フック
 */
export const useLaneDetailEdit = (
  detail: ShipmentOrderLaneDetail | null | undefined,
): UseLaneDetailEditReturn => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutate, isPending } = useMutationShipmentOrderLane();

  const [isEditing, setIsEditing] = useState(false);
  const [requiredTruckCount, setRequiredTruckCount] = useState<number | null>(null);
  const [berthReservations, setBerthReservations] = useState<EditableBerthReservation[]>([]);
  const [manualAssignments, setManualAssignments] = useState<EditableAssignment[]>([]);
  const [modified, setModified] = useState(false);

  useEffect(() => {
    if (!detail) return;

    if (requiredTruckCount > 0 && detail.requiredTruckCount !== requiredTruckCount) {
      setModified(true);
    }
  }, [requiredTruckCount, detail]);

  // 詳細データが変更されたら編集データをリセット
  useEffect(() => {
    if (detail) {
      setRequiredTruckCount(detail.requiredTruckCount);
      setBerthReservations(
        (detail.laneBerthReservations || []).map((r) => ({
          ...r,
          tempId: `berth-${r.id}`,
        }))
      );
      setManualAssignments(
        (detail.laneManualAssignments || []).map((a) => ({
          ...a,
          tempId: `assignment-${a.id}`,
        }))
      );
    }
  }, [detail]);

  useEffect(() => {
    if (!detail) return;
    if (requiredTruckCount === null || requiredTruckCount < 0) {
      return;
    }
    if (manualAssignments.length > requiredTruckCount) {
      setManualAssignments((prev) => prev.slice(0, requiredTruckCount));
      return;
    }
    const baseQuantity = Math.floor(detail.volumeSummary.packages / requiredTruckCount);
    const baseWeight = Math.floor(detail.volumeSummary.weightKg / requiredTruckCount);
    const baseVolume = Math.floor(detail.volumeSummary.volumeM3 / requiredTruckCount);

    const reminderQuantity = detail.volumeSummary.packages % baseQuantity;
    const reminderWeight = detail.volumeSummary.weightKg % baseWeight;
    const reminderVolume = detail.volumeSummary.volumeM3 % baseVolume;

    for (let i = manualAssignments.length; i < requiredTruckCount; i++) {
      setManualAssignments((prev) => [
        ...prev,
        { tempId: `assignment-${Date.now()}`} as EditableAssignment,
      ].map((assignment, index) => ({
        ...assignment,
        quantity: baseQuantity + (index === prev.length && reminderQuantity > 0 ? reminderQuantity : 0),
        weightKg: baseWeight + (index === prev.length && reminderWeight > 0 ? reminderWeight : 0),
        volumeM3: baseVolume + (index === prev.length && reminderVolume > 0 ? reminderVolume : 0),
      })));
    }
  }, [detail, manualAssignments.length, requiredTruckCount]);

  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  const cancelEditing = useCallback(() => {
    // 編集データをリセット
    if (detail) {
      setRequiredTruckCount(detail.requiredTruckCount);
      setBerthReservations(
        (detail.laneBerthReservations || []).map((r) => ({
          ...r,
          tempId: `berth-${r.id}`,
        }))
      );
      setManualAssignments(
        (detail.laneManualAssignments || []).map((a) => ({
          ...a,
          tempId: `assignment-${a.id}`,
        }))
      );
    }
    setIsEditing(false);
  }, [detail]);

  const saveChanges = useCallback(() => {
    if (!detail) return;

    const request: UpdateShipmentOrderLaneRequest = {
      id: detail.id,
      requiredTruckCount,
      berthReservations: berthReservations.map(({ tempId: _, ...rest }) => rest),
      manualAssignments: manualAssignments.map(({ tempId: _, ...rest }) => rest),
    };

    mutate(request, {
      onSuccess: () => {
        enqueueSnackbar('レーン情報を更新しました', { variant: 'success' });
        setIsEditing(false);
        setModified(false);
      },
      onError: (error) => {
        enqueueSnackbar(
          `更新に失敗しました: ${error.message || '不明なエラー'}`,
          { variant: 'error' }
        );
      },
    });
  }, [detail, requiredTruckCount, berthReservations, manualAssignments, mutate, enqueueSnackbar]);

  const updateBerthReservation = useCallback(
    (tempId: string, field: keyof Omit<LaneBerthReservation, 'id'>, value: string | null) => {
      setModified(true);
      let processedValue = value;
      if ((field === 'reservedFrom' || field === 'reservedTo') && value && detail?.loadingOn) {
        const dateTime = datetimeDecorator.fromHourMinutesToDate(value, detail.loadingOn);
        processedValue = dateTime ? dateTime.toISOString() : value;
      }
      setBerthReservations((prev) =>
        prev.map((r) => (r.tempId === tempId ? { ...r, [field]: processedValue } : r))
      );
    },
    [detail?.loadingOn]
  );

  const addBerthReservation = useCallback((kind: 'loading' | 'unloading') => {
    const newReservation: EditableBerthReservation = {
      id: undefined,
      tempId: `new-${Date.now()}`,
      kind,
      reservationNumber: null,
      reservedFrom: null,
      reservedTo: null,
      carrierCompany: null,
      truckNumber: null,
      driverName: null,
      driverContact: null,
    };
    setBerthReservations((prev) => [...prev, newReservation]);
    setModified(true);
  }, []);

  const removeBerthReservation = useCallback((tempId: string) => {
    setBerthReservations((prev) => prev.filter((r) => r.tempId !== tempId));
    setModified(true);
  }, []);

  const updateManualAssignment = useCallback(
    (tempId: string, field: keyof Omit<LaneManualAssignment, 'id'>, value: string | number | null) => {
      setManualAssignments((prev) =>
        prev.map((a) => (a.tempId === tempId ? { ...a, [field]: value } : a))
      );
      setModified(true);
    },
    []
  );

  const addManualAssignment = useCallback(() => {
    const newAssignment: EditableAssignment = {
      id: undefined,
      tempId: `new-${Date.now()}`,
      sequenceNumber: manualAssignments.length + 1,
      quantity: null,
      weightG: null,
      weightKg: null,
      volumeMm3: null,
      volumeM3: null,
      carrierCompany: null,
      operatingCarrier: null,
      driverName: null,
      truckNumber: null,
      hierarchyLevel: null,
    };
    setManualAssignments((prev) => [...prev, newAssignment]);
    setModified(true);
  }, [manualAssignments.length]);

  const removeManualAssignment = useCallback((tempId: string) => {
    setManualAssignments((prev) => {
      const filtered = prev.filter((a) => a.tempId !== tempId);
      // sequenceNumber を再採番
      return filtered.map((a, index) => ({
        ...a,
        sequenceNumber: index + 1,
      }));
    });
    setModified(true);
  }, []);

  return {
    isEditing,
    isSaving: isPending,
    requiredTruckCount,
    berthReservations,
    manualAssignments,
    modified,
    startEditing,
    cancelEditing,
    saveChanges,
    setRequiredTruckCount,
    updateBerthReservation,
    addBerthReservation,
    removeBerthReservation,
    updateManualAssignment,
    addManualAssignment,
    removeManualAssignment,
  };
};
